import { ReactNode } from "react";

interface HeroSectionProps {
  image: string;
  alt: string;
  title?: string;
  gradientPreset?: 'fade-top' | 'fade-bottom' | 'fade-center' | 'fade-dual' | 'none';
  imageOpacity?: number;
  overlayColor?: 'black' | 'background' | 'primary';
  className?: string;
  children: ReactNode;
}

const HeroSection = ({
  image,
  alt,
  title,
  gradientPreset = 'fade-top',
  imageOpacity = 40,
  overlayColor = 'background',
  className = '',
  children
}: HeroSectionProps) => {
  const gradients = {
    'fade-top': 'from-black/100 via-transparent to-transparent',
    'fade-bottom': 'from-transparent via-transparent to-black/100',
    'fade-center': 'from-black/80 via-transparent to-black/80',
    'fade-dual': 'from-black/100 via-transparent to-black/100',
    'none': ''
  };

  const overlayGradients = {
    'fade-top': 'from-background/60 via-background/80 to-background',
    'fade-bottom': 'from-transparent via-background/20 via-background/60 via-background/90 to-background',
    'fade-center': 'from-background/90 via-background/50 to-background/90',
    'fade-dual': 'from-background via-background/30 to-background',
    'none': ''
  };

  const opacityClass = `opacity-${imageOpacity}`;
  const gradientClass = gradients[gradientPreset];
  const overlayGradientClass = overlayColor === 'background' ? overlayGradients[gradientPreset] : '';

  return (
    <section className={`relative ${className}`}>
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <picture>
          <img 
            src={image}
            alt={alt}
            title={title}
            className={`w-full h-full object-cover ${opacityClass}`}
            loading="eager"
            fetchPriority="high"
            width={1920}
            height={1080}
            style={
              gradientPreset === 'fade-bottom' 
                ? {
                    maskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 80%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 80%)'
                  }
                : undefined
            }
          />
        </picture>
        
        {/* Combined Overlays - reduziert von 5 auf 2 Layers */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(to bottom, 
                rgba(0, 0, 0, 0.55) 0%,
                rgba(0, 0, 0, 0.55) 70%,
                transparent 100%
              ),
              linear-gradient(to bottom,
                transparent 0%,
                hsl(var(--background) / 0.3) 70%,
                hsl(var(--background) / 0.7) 85%,
                hsl(var(--background)) 100%
              )
            `
          }}
        />
        
        {/* Subtile Trennlinie mit Primary-Farbe */}
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        
        {/* Gradient Overlays für spezielle Presets */}
        {gradientPreset !== 'none' && gradientPreset !== 'fade-bottom' && overlayColor === 'black' && (
          <div className={`absolute inset-0 bg-gradient-to-b ${gradientClass}`} />
        )}
        
        {gradientPreset !== 'none' && gradientPreset !== 'fade-bottom' && overlayColor === 'background' && (
          <div className={`absolute inset-0 bg-gradient-to-b ${overlayGradientClass}`} />
        )}
      </div>
      
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </section>
  );
};

export default HeroSection;
